import { useMemo, useState, type FormEvent } from 'react'
import { Loader2, MessageSquareOff, Plus, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { LoadError } from '@/components/campaigns/LoadError'
import { toTimeInput } from '@/components/campaigns/campaignFormat'
import {
  campaignStepTemplates,
  DEFAULT_CADENCE,
  DEMAND_VARIABLES,
  emptyStep,
  hasDynamicUrlButton,
  mappingForPositions,
  MAX_STEPS,
  templateBodyText,
  templateKey,
  templateVariablePositions,
} from '@/components/campaigns/campaignTemplates'
import { hasStepErrors, validateSteps, type StepErrors } from '@/components/campaigns/campaignValidation'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { EmptyState } from '@/components/ui/empty-state'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useCampaignSettingsQuery, useReplaceCampaignSteps, useWabaTemplatesQuery } from '@/queries'
import type { CampaignStep, CrmCampaign, VariableMappingEntry } from '@/services/crmCampaignsService'

const CONSTANT = '__constant'
const DEFAULT_SEND_TIME = '10:00'

function normalizeStep(step: CampaignStep): CampaignStep {
  return { ...step, hora_envio: toTimeInput(step.hora_envio) }
}

/** Paso 3 del editor: qué mensaje sale y cuándo. Hasta diez, cada uno con su plantilla. */
export function StepsEditor({ campaign, onSaved }: { campaign: CrmCampaign; onSaved?: () => void }) {
  const { t } = useTranslation()
  const settings = useCampaignSettingsQuery()
  const templatesQuery = useWabaTemplatesQuery(settings.data?.waba_id)
  const save = useReplaceCampaignSteps(campaign.id)
  const [steps, setSteps] = useState<CampaignStep[]>(() =>
    campaign.steps.length > 0 ? campaign.steps.map(normalizeStep) : [emptyStep(1, 0, DEFAULT_SEND_TIME)],
  )
  const [errors, setErrors] = useState<StepErrors[]>([])

  const templates = useMemo(() => campaignStepTemplates(templatesQuery.data), [templatesQuery.data])

  const patchStep = (index: number, patch: Partial<CampaignStep>) =>
    setSteps((prev) => prev.map((step, position) => (position === index ? { ...step, ...patch } : step)))

  const addStep = () =>
    setSteps((prev) => {
      const last = prev[prev.length - 1]
      return [...prev, emptyStep(prev.length + 1, last ? last.dia_offset + 1 : 0, last?.hora_envio ?? DEFAULT_SEND_TIME)]
    })

  const removeStep = (index: number) =>
    setSteps((prev) => prev.filter((_, position) => position !== index).map((step, position) => ({ ...step, orden: position + 1 })))

  const applyCadence = () =>
    setSteps((prev) => {
      const base = prev.map((step, index) =>
        index < DEFAULT_CADENCE.length ? { ...step, dia_offset: DEFAULT_CADENCE[index] } : step,
      )
      const sendTime = prev[0]?.hora_envio ?? DEFAULT_SEND_TIME
      for (let index = base.length; index < DEFAULT_CADENCE.length; index += 1) {
        base.push(emptyStep(index + 1, DEFAULT_CADENCE[index], sendTime))
      }
      return base
    })

  const selectTemplate = (index: number, key: string) => {
    const template = templates.find((item) => templateKey(item.name, item.language) === key)
    if (!template) return
    patchStep(index, {
      plantilla_nombre: template.name,
      plantilla_idioma: template.language,
      mapeo_variables: mappingForPositions(templateVariablePositions(template), steps[index].mapeo_variables),
      ...(hasDynamicUrlButton(template) ? {} : { enlace_buscador: false, enlace_fijo: '' }),
    })
  }

  const patchVariable = (index: number, position: number, patch: Partial<VariableMappingEntry>) =>
    patchStep(index, {
      mapeo_variables: steps[index].mapeo_variables.map((entry) =>
        entry.position === position ? { ...entry, ...patch } : entry,
      ),
    })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!settings.data) return
    const found = validateSteps(
      steps,
      templates,
      { start: toTimeInput(settings.data.hora_inicio), end: toTimeInput(settings.data.hora_fin) },
      t,
    )
    setErrors(found)
    if (found.some(hasStepErrors)) return
    save.mutate(steps.map((step, index) => ({ ...step, orden: index + 1 })), {
      onSuccess: () => {
        toast.success(t('campaigns.steps.saved'))
        onSaved?.()
      },
    })
  }

  if (settings.isPending || (settings.data && templatesQuery.isPending)) {
    return (
      <div className="space-y-2" data-testid="steps-loading">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }
  if (settings.isError) return <LoadError error={settings.error} onRetry={() => void settings.refetch()} />
  if (templatesQuery.isError) return <LoadError error={templatesQuery.error} onRetry={() => void templatesQuery.refetch()} />
  if (templates.length === 0) {
    return (
      <EmptyState
        icon={<MessageSquareOff className="h-6 w-6" />}
        title={t('campaigns.steps.no_templates_title')}
        description={t('campaigns.steps.no_templates_description')}
      />
    )
  }

  return (
    <form className="space-y-8" onSubmit={submit} noValidate>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" disabled={steps.length >= MAX_STEPS} onClick={addStep}>
          <Plus className="mr-2 h-4 w-4" />
          {t('campaigns.steps.add')}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={applyCadence}>
          {t('campaigns.steps.use_cadence', { days: DEFAULT_CADENCE.join(', ') })}
        </Button>
        <span className="text-xs text-muted-foreground">
          {t('campaigns.steps.count', { count: steps.length, max: MAX_STEPS })}
        </span>
      </div>

      <ol className="space-y-4">
        {steps.map((step, index) => {
          const template = templates.find(
            (item) => item.name === step.plantilla_nombre && item.language === step.plantilla_idioma,
          )
          const stepErrors = errors[index] ?? {}
          const fieldId = (name: string) => `step-${index}-${name}`
          return (
            <li key={index} className="space-y-4 rounded-lg border bg-card p-4">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-medium">{t('campaigns.steps.step_title', { number: index + 1 })}</h3>
                {steps.length > 1 ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    aria-label={t('campaigns.steps.remove', { number: index + 1 })}
                    title={t('campaigns.steps.remove', { number: index + 1 })}
                    onClick={() => removeStep(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                ) : null}
              </div>

              <div className="grid gap-4 md:grid-cols-[1fr_6rem_8rem]">
                <div className="space-y-1.5">
                  <Label htmlFor={fieldId('template')}>{t('campaigns.steps.template_label')}</Label>
                  <Select
                    value={template ? templateKey(template.name, template.language) : undefined}
                    onValueChange={(key) => selectTemplate(index, key)}
                  >
                    <SelectTrigger id={fieldId('template')} aria-invalid={Boolean(stepErrors.template)}>
                      <SelectValue placeholder={t('campaigns.steps.template_placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map((item) => (
                        <SelectItem key={templateKey(item.name, item.language)} value={templateKey(item.name, item.language)}>
                          {item.name} ({item.language})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FieldError>{stepErrors.template}</FieldError>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={fieldId('day')}>{t('campaigns.steps.day_label')}</Label>
                  <Input
                    id={fieldId('day')}
                    aria-label={t('campaigns.steps.day_label')}
                    type="number"
                    min={0}
                    value={step.dia_offset}
                    aria-invalid={Boolean(stepErrors.day)}
                    onChange={(event) => patchStep(index, { dia_offset: Number(event.target.value) })}
                  />
                  <FieldError>{stepErrors.day}</FieldError>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={fieldId('time')}>{t('campaigns.steps.time_label')}</Label>
                  <Input
                    id={fieldId('time')}
                    aria-label={t('campaigns.steps.time_label')}
                    type="time"
                    value={step.hora_envio}
                    aria-invalid={Boolean(stepErrors.time)}
                    onChange={(event) => patchStep(index, { hora_envio: event.target.value })}
                  />
                  <FieldError>{stepErrors.time}</FieldError>
                </div>
              </div>

              {template ? (
                <p className="whitespace-pre-wrap rounded-md bg-muted/40 p-2 text-xs text-muted-foreground">
                  {templateBodyText(template)}
                </p>
              ) : null}

              {step.mapeo_variables.map((entry) => {
                const variableError = stepErrors.variables?.[entry.position]
                const selectId = fieldId(`variable-${entry.position}`)
                const isConstant = entry.source_type === 'constant'
                return (
                  <div key={entry.position} className="grid gap-2 md:grid-cols-[4rem_1fr_1fr] md:items-start">
                    <Label htmlFor={selectId} className="font-mono md:pt-2">{`{{${entry.position}}}`}</Label>
                    <div className="space-y-1.5">
                      <Select
                        value={isConstant ? CONSTANT : entry.source_key || undefined}
                        onValueChange={(value) =>
                          patchVariable(
                            index,
                            entry.position,
                            value === CONSTANT
                              ? { source_type: 'constant', source_key: '', fallback_text: undefined }
                              : { source_type: 'crm_demand', source_key: value },
                          )
                        }
                      >
                        <SelectTrigger id={selectId} aria-invalid={Boolean(variableError)}>
                          <SelectValue placeholder={t('campaigns.steps.variable_placeholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          {DEMAND_VARIABLES.map((key) => (
                            <SelectItem key={key} value={key}>
                              {t(`campaigns.steps.variable.${key}`)}
                            </SelectItem>
                          ))}
                          <SelectItem value={CONSTANT}>{t('campaigns.steps.variable_constant')}</SelectItem>
                        </SelectContent>
                      </Select>
                      <FieldError>{variableError}</FieldError>
                    </div>
                    {isConstant ? (
                      <Input
                        aria-label={t('campaigns.steps.constant_label', { position: entry.position })}
                        placeholder={t('campaigns.steps.constant_placeholder')}
                        value={entry.source_key}
                        onChange={(event) => patchVariable(index, entry.position, { source_key: event.target.value })}
                      />
                    ) : (
                      <Input
                        aria-label={t('campaigns.steps.fallback_label', { position: entry.position })}
                        placeholder={t('campaigns.steps.fallback_placeholder')}
                        value={entry.fallback_text ?? ''}
                        onChange={(event) =>
                          patchVariable(index, entry.position, { fallback_text: event.target.value || undefined })
                        }
                      />
                    )}
                  </div>
                )
              })}

              {hasDynamicUrlButton(template) ? (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 font-normal">
                    <Checkbox
                      aria-label={t('campaigns.steps.search_link_label')}
                      checked={step.enlace_buscador}
                      onCheckedChange={(checked) => patchStep(index, { enlace_buscador: checked === true })}
                    />
                    {t('campaigns.steps.search_link_label')}
                  </Label>
                  <FieldError>{stepErrors.link}</FieldError>
                  <div className="space-y-1.5">
                    <Label htmlFor={fieldId('fixed-link')}>{t('campaigns.steps.fixed_link_label')}</Label>
                    <Input
                      id={fieldId('fixed-link')}
                      aria-label={t('campaigns.steps.fixed_link_label')}
                      maxLength={1000}
                      placeholder={t('campaigns.steps.fixed_link_placeholder')}
                      value={step.enlace_fijo}
                      onChange={(event) => patchStep(index, { enlace_fijo: event.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">{t('campaigns.steps.fixed_link_help')}</p>
                  </div>
                </div>
              ) : null}
            </li>
          )
        })}
      </ol>

      {save.isError ? (
        <Alert variant="destructive">
          <AlertDescription>{save.error.message}</AlertDescription>
        </Alert>
      ) : null}

      <Button type="submit" disabled={save.isPending}>
        {save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        {t('campaigns.steps.save')}
      </Button>
    </form>
  )
}
